import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";

const PROFILE_IMG = "/assets/profile.jpeg";

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.15 },
  },
};

const item = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Top meta strip */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-zinc-500"
        >
          <span data-testid="hero-meta-availability">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 mr-2 align-middle" />
            Yogyakarta &middot; ID
          </span>
          <span className="hidden sm:inline">MMXXVI</span>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-end">
        {/* LEFT: Text */}
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="lg:col-span-7 order-2 lg:order-1"
        >
          <motion.p
            variants={item}
            className="text-xs uppercase tracking-[0.3em] text-[#C09D64] mb-6"
            data-testid="hero-eyebrow"
          >
            (01) — Medical Doctor / Builder
          </motion.p>

          <motion.h1
            variants={item}
            data-testid="hero-name"
            className="font-serif text-5xl md:text-7xl lg:text-[6.25rem] leading-[0.92] tracking-tight text-white"
          >
            dr. Mohammad
            <br />
            <span className="italic font-normal text-zinc-200">
              Bintang
            </span>{" "}
            <span className="text-zinc-400">Bis</span>
            <br />
            Cirurgiputra.
          </motion.h1>

          <motion.div
            variants={item}
            className="mt-10 md:mt-12 max-w-xl"
          >
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-zinc-500 mb-5">
              <span className="h-px w-8 bg-zinc-700" />
              <span data-testid="hero-tagline">Let me return your time.</span>
            </div>
            <p
              data-testid="hero-bio"
              className="text-base md:text-lg text-zinc-400 leading-relaxed text-balance"
            >
              A medical doctor obsessed with AI and emerging tech — building
              automations and AI agents that compress repetitive daily work, so
              clinicians and teams get their hours back.
            </p>
          </motion.div>

          <motion.div
            variants={item}
            className="mt-10 md:mt-12 flex flex-wrap items-center gap-3"
          >
            <a
              href="#projects"
              data-testid="hero-cta-work"
              className="group inline-flex items-center gap-3 bg-white text-black rounded-full pl-5 pr-2 py-2 text-sm font-medium hover:bg-[#C09D64] transition-colors duration-300"
            >
              See selected work
              <span className="w-8 h-8 rounded-full bg-black text-white inline-flex items-center justify-center group-hover:rotate-45 transition-transform duration-300">
                <ArrowDownRight className="w-4 h-4" />
              </span>
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-contact"
              className="inline-flex items-center gap-2 text-sm text-zinc-300 border border-white/15 rounded-full px-5 py-2.5 hover:bg-white/[0.04] transition-colors duration-300"
            >
              Get in touch
            </a>
          </motion.div>
        </motion.div>

        {/* RIGHT: Portrait */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 order-1 lg:order-2 relative"
        >
          <div className="relative aspect-[4/5] w-full max-w-md mx-auto lg:max-w-none lg:ml-auto">
            {/* Portrait blends into the black bg */}
            <img
              src={PROFILE_IMG}
              alt="dr. Mohammad Bintang Bis Cirurgiputra"
              data-testid="hero-portrait"
              className="absolute inset-0 w-full h-full object-cover object-top select-none pointer-events-none"
              draggable={false}
            />
            {/* Soft top fade so it dissolves into the page */}
            <div
              className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black to-transparent"
              aria-hidden
            />
            {/* Side fade for editorial blend */}
            <div
              className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-black to-transparent"
              aria-hidden
            />
            {/* Identification caption */}
            <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              <span>Portrait / MMXXVI</span>
              <span className="text-zinc-400">№ 001</span>
            </div>
          </div>

          {/* Corner ticks */}
          <div className="absolute -top-2 -left-2 w-6 h-6 border-t border-l border-white/20" />
          <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b border-r border-white/20" />
        </motion.div>
      </div>

      {/* Marquee-style bottom strip */}
      <div className="mt-20 md:mt-28 border-y border-white/[0.06] py-6 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex flex-wrap items-center justify-between gap-x-10 gap-y-3 text-[11px] uppercase tracking-[0.25em] text-zinc-500">
          <span>Medical Practice</span>
          <span className="text-zinc-700">·</span>
          <span>AI Agents</span>
          <span className="text-zinc-700">·</span>
          <span>Automation</span>
          <span className="text-zinc-700">·</span>
          <span>Workflow Engineering</span>
          <span className="text-zinc-700">·</span>
          <span>Research</span>
        </div>
      </div>
    </section>
  );
}
